import { useState, useEffect } from "react";
import { X, Lock, Check, Sparkles } from "lucide-react";
import axios from "axios";
import { API, SECONDARY_LANGUAGES } from "./constants";
import { getDeviceId } from "./deviceId";
import { ptAuthHeaders } from "./poodletalk/ptAuth";

/**
 * Bilingual language picker. Paid families can choose up to 2 second languages
 * — the poodle then mirrors whichever language the child uses in chat.
 * Free users see padlocks with an upgrade prompt.
 */
const LanguageToggleModal = ({ isOpen, onClose, onSaved, paidRemaining = 0 }) => {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  const isPaid = paidRemaining > 0;
  const MAX = 2;

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setSavedMsg("");
    axios
      .get(`${API}/languages`, {
        params: { device_id: getDeviceId() },
        headers: ptAuthHeaders(),
      })
      .then((res) => {
        if (cancelled) return;
        setSelected(res.data.selected || []);
      })
      .catch(() => {
        if (cancelled) return;
        setSelected([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleLang = (code) => {
    if (!isPaid) {
      setError("Pick up to 2 second languages once you add chat credits 💛");
      return;
    }
    setError("");
    setSavedMsg("");
    setSelected((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      if (prev.length >= MAX) {
        setError(`You can pick up to ${MAX} languages. Untick one to swap.`);
        return prev;
      }
      return [...prev, code];
    });
  };

  const handleSave = async () => {
    if (!isPaid) return;
    setSaving(true);
    setError("");
    setSavedMsg("");
    try {
      const res = await axios.post(
        `${API}/languages`,
        { device_id: getDeviceId(), selected },
        { headers: ptAuthHeaders() }
      );
      setSelected(res.data.selected || []);
      setSavedMsg("Saved 💛");
      if (onSaved) onSaved(res.data.selected || []);
      setTimeout(() => {
        setSavedMsg("");
        onClose();
      }, 900);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Could not save. Please try again.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="language-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#FDFBF7] rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="language-modal-close"
          className="absolute top-3 right-3 text-[#6B6B6B] hover:text-[#4A4A4A]"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="mb-4">
          <h2 className="text-2xl font-bold text-[#4A4A4A] flex items-center gap-2">
            <span>🌍</span> My Languages
          </h2>
          <p className="text-sm text-[#6B6B6B] mt-1">
            {isPaid
              ? "Pick up to 2 second languages. Your poodle will reply in whichever language you write in. 💛"
              : "Bilingual chat is a paid perk — add chat credits to unlock up to 2 second languages."}
          </p>
        </div>

        <div className="bg-[#FFF7D6] rounded-2xl px-3 py-2 mb-4 flex items-center gap-2">
          <Check className="w-4 h-4 text-[#B89823]" />
          <span className="text-sm text-[#4A4A4A]">
            English <span className="text-[#9A9A9A]">— always on</span>
          </span>
        </div>

        {loading ? (
          <div className="text-center text-[#9A9A9A] py-6 text-sm">Loading…</div>
        ) : (
          <div className="space-y-2" data-testid="language-list">
            {SECONDARY_LANGUAGES.map((lang) => {
              const checked = selected.includes(lang.code);
              const disabled = !isPaid;
              return (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => toggleLang(lang.code)}
                  data-testid={`language-option-${lang.code}`}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-all text-left ${
                    checked
                      ? "bg-[#E8F5E9] border-[#9CCC9C]"
                      : "bg-white border-[#EAE3D2] hover:border-[#D6CFBF]"
                  } ${disabled ? "opacity-80" : "hover:scale-[1.01]"}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{lang.flag}</span>
                    <div>
                      <div className="text-[#4A4A4A] font-medium">{lang.native}</div>
                      <div className="text-xs text-[#9A9A9A]">{lang.label}</div>
                    </div>
                  </div>
                  {disabled ? (
                    <div className="flex items-center gap-1 text-[#A55B64]">
                      <Lock className="w-4 h-4" />
                      <span className="text-xs font-medium">Premium</span>
                    </div>
                  ) : checked ? (
                    <div className="w-6 h-6 rounded-full bg-[#5A8A5A] text-white flex items-center justify-center">
                      <Check className="w-4 h-4" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-[#D6CFBF]" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {error && (
          <p className="text-sm text-[#A55B64] mt-3" data-testid="language-error">
            {error}
          </p>
        )}
        {savedMsg && (
          <p className="text-sm text-[#5A8A5A] mt-3" data-testid="language-saved">
            {savedMsg}
          </p>
        )}

        <div className="mt-5 flex flex-col gap-2">
          {isPaid ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="language-save-button"
              className="w-full bg-[#A55B64] hover:bg-[#8A4951] disabled:opacity-50 text-white py-3 rounded-full font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              {saving ? "Saving…" : "Save my languages"}
            </button>
          ) : (
            <div className="bg-gradient-to-br from-[#FFE5C2] to-[#FCEAEA] rounded-2xl p-4 text-sm text-[#4A4A4A]">
              <strong>Why bilingual?</strong> Many families speak two languages at home.
              Your poodle can reply in {SECONDARY_LANGUAGES.map((l) => l.flag).join(" ")} —
              gentle, age-appropriate, and safe in every language.
              <div className="mt-3 text-xs text-[#6B6B6B]">
                Add chat credits in the Parents&apos; Corner to unlock this.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LanguageToggleModal;
